import type { ClassProfile, GameConfig, GameMode, GameResult } from '../types';
import { getSettings, saveSettings } from '../storage/settings';
import { getProfiles, getActiveProfile, setActiveProfileId } from '../storage/profiles';
import { HomeScreen } from './HomeScreen';
import { RosterScreen } from './RosterScreen';
import { GameScreen } from './GameScreen';
import { ClassModal } from './ClassModal';
import { OnlineModal } from './OnlineModal';
import { GuideModal } from './GuideModal';
import { CustomMapModal } from './CustomMapModal';

export type Screen = 'home' | 'roster' | 'game' | 'class-mgmt' | 'online' | 'map-custom' | 'guide';

export interface NavData {
  mode?: GameMode;
  config?: GameConfig;
  result?: GameResult;
  profile?: ClassProfile;
  joinCode?: string;
}

export class App {
  private root: HTMLElement;
  private currentScreen: { destroy(): void } | null = null;
  private overlay: HTMLElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.applyTheme();
    this.setupKeyboardShortcuts();
    this.navigate('home');
    const params = new URLSearchParams(location.search);
    const joinCode = params.get('join');
    if (joinCode) this.navigate('online', { joinCode });

    const showGuide = params.get('guide');
    if (showGuide === 'true') this.navigate('guide');
  }

  navigate(screen: Screen, data: NavData = {}) {
    this.currentScreen?.destroy();
    this.currentScreen = null;
    if (this.overlay && screen !== 'online' && screen !== 'class-mgmt' && screen !== 'map-custom' && screen !== 'guide') {
      this.overlay.remove();
      this.overlay = null;
    }
    this.root.innerHTML = '';

    switch (screen) {
      case 'home':
        this.currentScreen = new HomeScreen(this.root, this);
        break;
      case 'roster':
        this.currentScreen = new RosterScreen(this.root, this, data.mode!);
        break;
      case 'game':
        this.currentScreen = new GameScreen(this.root, this, data.config!);
        break;
      case 'class-mgmt':
        this.showClassModal();
        break;
      case 'online':
        this.showOnlineModal(data.mode, data.joinCode);
        break;
      case 'map-custom':
        this.showCustomMapModal();
        break;
      case 'guide':
        this.showGuide();
        break;
    }
  }

  private showClassModal() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';
    document.body.appendChild(this.overlay);
    const modal = new ClassModal(this.overlay, this);
    this.currentScreen = modal;
  }

  private showOnlineModal(mode?: GameMode, joinCode?: string) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';
    document.body.appendChild(this.overlay);
    const modal = new OnlineModal(this.overlay, this, mode ?? 'single', { initialCode: joinCode ?? undefined });
    this.currentScreen = modal;
  }

  private showCustomMapModal() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';
    document.body.appendChild(this.overlay);
    const modal = new CustomMapModal(this.overlay, this);
    this.currentScreen = modal;
  }

  private showGuide() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';
    document.body.appendChild(this.overlay);
    const modal = new GuideModal(this.overlay, this);
    this.currentScreen = modal as any;
  }

  closeOverlay() {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.currentScreen = null;
    this.navigate('home');
  }

  applyTheme() {
    const settings = getSettings();
    const theme = settings.theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : settings.theme;
    document.documentElement.setAttribute('data-theme', theme);
  }

  toggleTheme() {
    const settings = getSettings();
    const current = document.documentElement.getAttribute('data-theme') ?? 'light';
    const next = current === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    saveSettings({ theme: next });
  }

  getActiveProfile(): ClassProfile | null {
    return getActiveProfile();
  }

  getProfiles(): ClassProfile[] {
    return getProfiles();
  }

  setActiveProfile(id: string) {
    setActiveProfileId(id);
    saveSettings({ lastClassId: id });
  }

  private setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.overlay) this.closeOverlay();
      }
      if (e.key === 'F' || e.key === 'f') {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(() => {});
        } else {
          document.exitFullscreen().catch(() => {});
        }
      }
    });
  }
}
